import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/services/session';
import { Button } from '@/design-system/Button';
import { Card } from '@/design-system/Surfaces';
import { Logo } from '@/design-system/Logo';
import { cn } from '@/design-system/cn';
import { IconLock } from '@/features/studio/Icons';

/**
 * The adult gate (spec §6.3).
 *
 * IMPORTANT AND DELIBERATE: this is a *UX* boundary, not a security boundary.
 * The spec is explicit that "a PIN overlay alone cannot secure sensitive API
 * operations" — real protection needs server-side verification of a
 * short-lived adult grant, which cannot exist until there is a backend. The
 * screen says so rather than implying the data behind it is protected.
 *
 * The arithmetic challenge is the pattern Apple expects for a parental gate
 * in a kids app: something a pre-literate child cannot pass by tapping.
 */
export function ParentGate({ children }: { children: React.ReactNode }) {
  const hasGrant = useSession((s) => s.hasAdultGrant());
  const grantAdult = useSession((s) => s.grantAdult);
  const navigate = useNavigate();
  const [answer, setAnswer] = useState('');
  const [wrong, setWrong] = useState(false);
  // Regenerated per mount so the answer is never memorable.
  const [a, b] = useMemo(() => [6 + Math.floor(Math.random() * 7), 6 + Math.floor(Math.random() * 7)], []);

  if (hasGrant) return <>{children}</>;

  const submit = () => {
    if (Number(answer) === a * b) {
      grantAdult();
      setWrong(false);
    } else {
      setWrong(true);
      setAnswer('');
    }
  };

  return (
    <div className="grid min-h-[100dvh] place-items-center bg-paper px-l">
      <Card className="w-full max-w-md text-center">
        <Logo size="sm" />
        <div className="mx-auto mt-l grid h-14 w-14 place-items-center rounded-full bg-primary-soft text-primary-deep">
          <IconLock />
        </div>
        <h1 className="mt-l font-display text-[24px] font-semibold text-ink">Grown-ups only</h1>
        <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
          Answer this to carry on.
        </p>

        <p className="mt-xl font-display text-[34px] font-semibold text-ink" aria-live="polite">
          {a} × {b} = ?
        </p>

        <label htmlFor="gate" className="u-sr">
          The answer to {a} times {b}
        </label>
        <input
          id="gate"
          inputMode="numeric"
          autoComplete="off"
          value={answer}
          onChange={(e) => {
            setAnswer(e.target.value.replace(/\D/g, '').slice(0, 3));
            setWrong(false);
          }}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className={cn(
            'mt-l min-h-touch-lg w-40 rounded-pebble border-2 bg-paper text-center font-display text-[26px] text-ink',
            wrong ? 'border-secondary' : 'border-line',
          )}
        />

        {wrong ? (
          <p role="alert" className="mt-m text-[15px] font-semibold text-secondary">
            Not quite — have another go.
          </p>
        ) : null}

        <Button variant="magic" size="lg" block className="mt-l" onClick={submit} disabled={!answer}>
          Continue
        </Button>
        <Button variant="ghost" size="md" block className="mt-s" onClick={() => navigate('/app')}>
          Back to profiles
        </Button>

        <p className="mt-xl border-t-2 border-dashed border-line pt-l text-[13px] leading-relaxed text-ink-faint">
          This keeps a child out of the grown-up screens. It is not security:
          the data is on this device, and anyone holding the device can reach it.
          Real protection means verifying that a grown-up is a grown-up, which is
          a process with a person in it, not a sum — and that is not built.
        </p>
      </Card>
    </div>
  );
}
